import React, { useState } from 'react';
import { Heart } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Comment } from '@/types/social';
import { formatRelativeTime } from '@/lib/utils/time';
import { toggleReplyLike, getReplyLikeUsers } from '@/lib/supabase/social';


import LikesModal from './LikesModal';
import { useEffect } from 'react';
import AvatarSkeleton from '@/components/ui/avatar/AvatarSkeleton';

interface CommentReplyProps {
  reply: Comment;
  onReply: () => void;
}

const CommentReply: React.FC<CommentReplyProps> = ({ reply, onReply }) => {
  const [isLiked, setIsLiked] = useState(reply.isLiked || false);
  const [likesCount, setLikesCount] = useState(reply.likes || 0);
  const [likeUsers, setLikeUsers] = useState<Array<{auth_id: string, full_name: string, avatar_url: string}>>([]);
  const [isLoadingLikes, setIsLoadingLikes] = useState(false);
  const [showLikesModal, setShowLikesModal] = useState(false);

  // Fetch users who liked the reply
  useEffect(() => {
    if (likesCount > 0) {
      const fetchLikeUsers = async () => {
        setIsLoadingLikes(true);
        try {
const users = await getReplyLikeUsers(reply.id);

          setLikeUsers(users.slice(0, 2)); // Get first 2 users
        } catch (error) {
          console.error('Error fetching reply like users:', error);
        } finally {
          setIsLoadingLikes(false);
        }
      };
      
      fetchLikeUsers();
    } else {
      setLikeUsers([]);
    }
  }, [reply.id, likesCount]);

  const handleLike = async () => {
    try {
      if (!reply || !reply.id) {
        console.error('Invalid reply data');
        return;
      }
      
const newIsLiked = await toggleReplyLike(reply.id);

      setIsLiked(newIsLiked);
      setLikesCount(prev => newIsLiked ? prev + 1 : Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  return (
    <div className="flex space-x-2">
      <img
        src={reply.author.image}
        alt={reply.author.name}
        className="w-6 h-6 rounded-full object-cover border border-dark-400 flex-shrink-0 bg-dark-300"
        onError={(e) => {
          e.currentTarget.src = 'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png';
        }}
      />
      
      <div className="flex-1">
        <div className="bg-dark-300 rounded-xl px-3 py-2">
          <div className="font-medium text-white text-sm">{reply.author.name}</div>
          <p className="text-white text-xs">{reply.content}</p>
        </div>
        
        <div className="flex items-center space-x-4 mt-1 px-2">
          <button 
            onClick={handleLike}
            className={cn(
              "text-xs transition-colors",
              isLiked ? "text-red-500" : "text-dark-800 hover:text-gold-400"
            )}
          >
            <span className="flex items-center">
              <Heart className={cn(
                "h-3 w-3 mr-1",
                isLiked && "fill-red-500"
              )} />
              <div className="flex items-center">
                {likesCount > 0 && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowLikesModal(true);
                    }}
                    className="hover:underline mr-1"
                  >
                    {likesCount}
                  </button>
                )}
                
                {/* User avatars for likes */}
                {likesCount > 0 && (
                  <div 
                    className="flex items-center cursor-pointer" 
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowLikesModal(true);
                    }}
                  >
                    {isLoadingLikes ? (
                      <AvatarSkeleton size={14} count={Math.min(2, likesCount)} />
                    ) : (
                      <>
                        <div className="flex -space-x-1 overflow-hidden">
                          {likeUsers.map((user) => (
                            <img
                              key={user.auth_id}
                              src={user.avatar_url || 'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png'}
                              alt={user.full_name}
                              className="inline-block h-3.5 w-3.5 rounded-full object-cover dark:border-dark-300/30 light:border-white/50 border shadow-sm"
                              title={user.full_name}
                            />
                          ))}
                        </div>
                        {likesCount > likeUsers.length && (
                          <span className="text-xs text-dark-800 ml-0.5">
                            +{likesCount - likeUsers.length}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              {likesCount === 0 && "Like"}
            </span>
          </button>
          
          <button 
            onClick={onReply}
            className="text-xs text-dark-800 hover:text-gold-400 transition-colors"
          >
            Reply
          </button>
          
          <span className="text-dark-800 text-xs">
            {formatRelativeTime(reply.createdAt)}
          </span>
          
          {/* Likes Modal */}
          <LikesModal
            isOpen={showLikesModal}
            onClose={() => setShowLikesModal(false)}
            commentId={reply.id}
            title={`${likesCount} ${likesCount === 1 ? 'Like' : 'Likes'}`}
          />
        </div>
      </div>
    </div>
  );
};

export default CommentReply;